/**
 * Version Progress Page Component
 *
 * Displays version processing progress for existing table uploads.
 * Uses the shared VersionProgressCard component for consistent UI.
 */

import { useCallback } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';

import { VersionProgressCard } from '@/components/common';
import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';

export function VersionProgressPage() {
  const search = useSearch({ from: '/_authenticated/_withWorkspace/versions/progress' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const rpc = useRpc();
  const versionId = search.versionId as string | undefined;

  const handleComplete = useCallback(
    async (version: { id: string; tableId: string; fileName: string }) => {
      // Invalidate versions cache so the table page shows the new version
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tables.versions(version.tableId),
      });
      // Also invalidate all versions list
      void queryClient.invalidateQueries({
        queryKey: queryKeys.versions.all,
      });

      // Fetch the table to get its folder_id for navigation
      let folderId: string | undefined;
      try {
        // @ts-expect-error - Hono RPC type inference limitation for nested routes
        const res = await rpc.api.private.tables[':id'].$get({
          param: { id: version.tableId },
        });
        if (res.ok) {
          const data = await res.json();
          folderId = data.table?.folder_id;
        }
      } catch {
        // Continue without folder navigation if fetch fails
      }

      // Navigate to workspace with the table's folder selected and preview pane open
      void navigate({
        to: '/workspace',
        search: {
          ...(folderId ? { folder: folderId } : {}),
          preview: version.tableId,
          type: 'table',
        },
      });
    },
    [queryClient, rpc, navigate]
  );

  const handleError = () => {
    void navigate({ to: '/versions/new', search: { tableId: undefined } });
  };

  return (
    <div className="absolute inset-4 top-0 flex flex-col overflow-hidden">
      <div className="mb-6 shrink-0">
        <h1 className="text-3xl font-bold tracking-tight">Version Progress</h1>
        <p className="text-muted-foreground">Track your version progress</p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <VersionProgressCard
          versionId={versionId}
          onComplete={handleComplete}
          onError={handleError}
          errorLabel="Try Again"
          showValidationErrors={true}
        />
      </div>
    </div>
  );
}
